import { useState, useEffect, useRef } from "react";

// ============================================================
// MVDL QUANTUM SUPERINTELLIGENCE PLATFORM
// Aesthetic: Dark cyberpunk-luxury with neon accents
// Fonts: Syne (display) + DM Mono (data) + Outfit (body)
// ============================================================

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #05050f;
    --bg2: #0a0a1a;
    --bg3: #0f0f22;
    --surface: #111128;
    --surface2: #181832;
    --border: rgba(100,100,200,0.15);
    --border2: rgba(100,100,200,0.3);
    --neon: #7c3aed;
    --neon2: #06b6d4;
    --neon3: #10b981;
    --neon4: #f59e0b;
    --neon5: #ec4899;
    --text: #e8e8f0;
    --text2: #9090b8;
    --text3: #5555888;
    --glow: 0 0 20px rgba(124,58,237,0.4);
    --glow2: 0 0 20px rgba(6,182,212,0.4);
    --r: 12px;
    --r2: 8px;
    --transition: 0.2s cubic-bezier(0.4,0,0.2,1);
  }

  html, body, #root { height: 100%; }
  body {
    font-family: 'Outfit', sans-serif;
    background: var(--bg);
    color: var(--text);
    overflow: hidden;
  }

  .app { display: flex; height: 100vh; overflow: hidden; }

  /* SIDEBAR */
  .sidebar {
    width: 72px;
    background: var(--bg2);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 0;
    gap: 4px;
    z-index: 100;
    flex-shrink: 0;
    transition: width var(--transition);
    overflow: hidden;
  }
  .sidebar.expanded { width: 220px; }
  .sidebar-logo {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 20px;
    color: var(--neon);
    letter-spacing: -0.5px;
    margin-bottom: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-shadow: var(--glow);
    cursor: pointer;
    padding: 0 16px;
    align-self: flex-start;
  }
  .sidebar-logo span { color: var(--neon2); }
  .nav-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-radius: var(--r2);
    cursor: pointer;
    transition: all var(--transition);
    white-space: nowrap;
    position: relative;
    color: var(--text2);
    font-size: 13px;
    font-weight: 500;
  }
  .nav-item:hover { background: var(--surface); color: var(--text); }
  .nav-item.active {
    background: linear-gradient(135deg, rgba(124,58,237,0.2), rgba(6,182,212,0.1));
    color: var(--neon2);
    border: 1px solid rgba(124,58,237,0.3);
  }
  .nav-item.active .nav-icon { color: var(--neon); }
  .nav-icon { font-size: 18px; flex-shrink: 0; width: 20px; text-align: center; }
  .nav-label { overflow: hidden; font-family: 'Outfit', sans-serif; }

  /* MAIN */
  .main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--bg);
  }

  /* TOPBAR */
  .topbar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--bg2);
    flex-shrink: 0;
  }
  .topbar-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 18px;
    flex: 1;
  }
  .topbar-search {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 6px 14px;
    width: 220px;
  }
  .topbar-search input {
    background: none;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 13px;
    font-family: 'Outfit', sans-serif;
    width: 100%;
  }
  .topbar-search input::placeholder { color: var(--text2); }
  .xp-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, rgba(124,58,237,0.2), rgba(6,182,212,0.1));
    border: 1px solid rgba(124,58,237,0.4);
    border-radius: 20px;
    padding: 4px 12px;
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: var(--neon2);
    cursor: pointer;
  }
  .avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--neon), var(--neon2));
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px; cursor: pointer;
    flex-shrink: 0;
  }

  /* CONTENT */
  .content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    scrollbar-width: thin;
    scrollbar-color: var(--border2) transparent;
  }

  /* CARDS */
  .card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r);
    padding: 20px;
    transition: border-color var(--transition);
  }
  .card:hover { border-color: var(--border2); }
  .card-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* GRID LAYOUTS */
  .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
  .grid-auto { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; }

  /* STAT CARDS */
  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r);
    padding: 20px;
    position: relative;
    overflow: hidden;
  }
  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--neon), var(--neon2));
  }
  .stat-label {
    font-size: 12px;
    color: var(--text2);
    font-family: 'DM Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }
  .stat-value {
    font-family: 'Syne', sans-serif;
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, var(--text), var(--neon2));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .stat-change {
    font-size: 12px;
    color: var(--neon3);
    margin-top: 4px;
    font-family: 'DM Mono', monospace;
  }

  /* BUTTONS */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: var(--r2);
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all var(--transition);
  }
  .btn-primary {
    background: linear-gradient(135deg, var(--neon), #6d28d9);
    color: white;
  }
  .btn-primary:hover {
    opacity: 0.9;
    box-shadow: var(--glow);
    transform: translateY(-1px);
  }
  .btn-secondary {
    background: var(--surface2);
    border: 1px solid var(--border2);
    color: var(--text);
  }
  .btn-secondary:hover { background: var(--surface); border-color: var(--neon); }
  .btn-ghost {
    background: transparent;
    color: var(--text2);
  }
  .btn-ghost:hover { color: var(--text); background: var(--surface); }
  .btn-sm { padding: 5px 10px; font-size: 12px; }
  .btn-icon { padding: 8px; width: 34px; height: 34px; justify-content: center; }

  /* TAGS/BADGES */
  .tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-family: 'DM Mono', monospace;
    font-weight: 500;
  }
  .tag-purple { background: rgba(124,58,237,0.2); color: #a78bfa; border: 1px solid rgba(124,58,237,0.3); }
  .tag-cyan { background: rgba(6,182,212,0.2); color: #67e8f9; border: 1px solid rgba(6,182,212,0.3); }
  .tag-green { background: rgba(16,185,129,0.2); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.3); }
  .tag-amber { background: rgba(245,158,11,0.2); color: #fcd34d; border: 1px solid rgba(245,158,11,0.3); }
  .tag-pink { background: rgba(236,72,153,0.2); color: #f9a8d4; border: 1px solid rgba(236,72,153,0.3); }

  /* PROGRESS */
  .progress-bar {
    width: 100%;
    height: 4px;
    background: var(--surface2);
    border-radius: 2px;
    overflow: hidden;
    margin-top: 8px;
  }
  .progress-fill {
    height: 100%;
    border-radius: 2px;
    background: linear-gradient(90deg, var(--neon), var(--neon2));
    transition: width 0.5s ease;
  }

  /* AI CHAT */
  .chat-container {
    display: flex;
    flex-direction: column;
    height: 400px;
    background: var(--bg);
    border-radius: var(--r2);
    overflow: hidden;
    border: 1px solid var(--border);
  }
  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }
  .chat-msg {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.6;
  }
  .chat-msg.user {
    align-self: flex-end;
    background: linear-gradient(135deg, var(--neon), #6d28d9);
    color: white;
    border-bottom-right-radius: 4px;
  }
  .chat-msg.ai {
    align-self: flex-start;
    background: var(--surface2);
    border: 1px solid var(--border);
    color: var(--text);
    border-bottom-left-radius: 4px;
  }
  .chat-msg.ai .ai-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: var(--neon2);
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .chat-input-row {
    display: flex;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid var(--border);
    background: var(--surface);
  }
  .chat-input {
    flex: 1;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 8px 12px;
    color: var(--text);
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    outline: none;
    resize: none;
  }
  .chat-input:focus { border-color: var(--neon); }

  /* TABLE */
  .data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .data-table th {
    text-align: left;
    padding: 10px 12px;
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: var(--text2);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid var(--border);
  }
  .data-table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
  }
  .data-table tr:hover td { background: var(--surface2); }
  .data-table tr:last-child td { border-bottom: none; }

  /* MODULE HEADER */
  .module-header {
    margin-bottom: 24px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }
  .module-title {
    font-family: 'Syne', sans-serif;
    font-size: 24px;
    font-weight: 800;
    background: linear-gradient(135deg, var(--text), var(--neon2));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .module-subtitle { color: var(--text2); font-size: 14px; margin-top: 4px; }

  /* TABS */
  .tabs {
    display: flex;
    gap: 2px;
    background: var(--surface);
    padding: 4px;
    border-radius: var(--r2);
    border: 1px solid var(--border);
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  .tab {
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all var(--transition);
    color: var(--text2);
    white-space: nowrap;
  }
  .tab.active {
    background: linear-gradient(135deg, var(--neon), #6d28d9);
    color: white;
    box-shadow: var(--glow);
  }
  .tab:not(.active):hover { color: var(--text); background: var(--surface2); }

  /* CANVAS */
  .quantum-canvas { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; }

  /* GAMIFICATION */
  .badge {
    width: 52px; height: 52px;
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
    border: 2px solid var(--border2);
    background: var(--surface2);
    cursor: pointer;
    transition: all var(--transition);
    position: relative;
  }
  .badge.earned { border-color: var(--neon4); box-shadow: 0 0 12px rgba(245,158,11,0.3); }
  .badge:hover { transform: scale(1.1); }

  /* NOTIFICATION DOT */
  .notif-dot {
    position: absolute;
    top: -2px; right: -2px;
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--neon5);
    border: 2px solid var(--bg2);
  }

  /* SCROLLABLE LIST */
  .list-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
  }
  .list-item:last-child { border-bottom: none; }

  /* INPUT */
  .input {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r2);
    padding: 8px 12px;
    color: var(--text);
    font-family: 'Outfit', sans-serif;
    font-size: 13px;
    outline: none;
    width: 100%;
    transition: border-color var(--transition);
  }
  .input:focus { border-color: var(--neon); }
  .input::placeholder { color: var(--text2); }

  /* SELECT */
  select.input { cursor: pointer; }
  select.input option { background: var(--surface2); }

  /* FORM */
  .form-group { margin-bottom: 16px; }
  .form-label {
    display: block;
    font-size: 12px;
    color: var(--text2);
    font-family: 'DM Mono', monospace;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }

  /* LOADING PULSE */
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  .pulse { animation: pulse 1.5s ease infinite; }

  /* GLOW BORDER ANIMATION */
  @keyframes glowBorder {
    0%,100% { border-color: rgba(124,58,237,0.2); }
    50% { border-color: rgba(6,182,212,0.5); box-shadow: 0 0 15px rgba(6,182,212,0.15); }
  }
  .glow-border { animation: glowBorder 3s ease infinite; }

  /* PARTICLE CANVAS WRAPPER */
  .canvas-wrapper { position: relative; overflow: hidden; border-radius: var(--r); }

  /* MOBILE */
  @media (max-width: 768px) {
    .sidebar { display: none; }
    .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; }
    .topbar-search { display: none; }
    .content { padding: 16px; }
    .mobile-nav {
      display: flex !important;
      position: fixed; bottom: 0; left: 0; right: 0;
      background: var(--bg2);
      border-top: 1px solid var(--border);
      padding: 8px 0 env(safe-area-inset-bottom, 8px);
      justify-content: space-around;
      z-index: 200;
    }
    .content { padding-bottom: 80px; }
  }
  .mobile-nav { display: none; }
  .mobile-nav-btn {
    display: flex; flex-direction: column; align-items: center;
    gap: 4px; padding: 6px 12px; cursor: pointer;
    font-size: 10px; color: var(--text2);
    transition: color var(--transition);
  }
  .mobile-nav-btn.active { color: var(--neon2); }
  .mobile-nav-btn span:first-child { font-size: 20px; }

  /* SPARKLINE */
  .sparkline { width: 100%; height: 40px; }

  /* TOGGLE */
  .toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }
  .toggle-row:last-child { border-bottom: none; }
  .toggle {
    width: 40px; height: 22px;
    border-radius: 11px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    position: relative;
    cursor: pointer;
    transition: all var(--transition);
    flex-shrink: 0;
  }
  .toggle.on { background: var(--neon); border-color: var(--neon); }
  .toggle::after {
    content: '';
    position: absolute;
    top: 2px; left: 2px;
    width: 16px; height: 16px;
    border-radius: 50%;
    background: white;
    transition: transform var(--transition);
  }
  .toggle.on::after { transform: translateX(18px); }

  /* TYPING INDICATOR */
  @keyframes typing { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
  .typing-dot { display:inline-block; width:6px; height:6px; background:var(--neon2); border-radius:50%; margin:0 2px; animation: typing 1.2s ease infinite; }
  .typing-dot:nth-child(2) { animation-delay: 0.2s; }
  .typing-dot:nth-child(3) { animation-delay: 0.4s; }

  /* SCROLL CUSTOM */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border2); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--neon); }

  /* SECTION */
  .section { margin-bottom: 24px; }

  /* DIVIDER */
  .divider { border: none; border-top: 1px solid var(--border); margin: 20px 0; }

  /* COLUMNS */
  .col-2 { display: flex; gap: 16px; }
  .col-2 > * { flex: 1; }

  /* MEDIA CARD */
  .media-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r);
    overflow: hidden;
    cursor: pointer;
    transition: all var(--transition);
  }
  .media-card:hover { transform: translateY(-2px); border-color: var(--border2); }
  .media-thumb {
    height: 140px;
    display: flex; align-items: center; justify-content: center;
    font-size: 48px;
    position: relative;
  }
  .media-info { padding: 12px; }
  .media-title { font-weight: 600; font-size: 13px; margin-bottom: 4px; }
  .media-meta { font-size: 11px; color: var(--text2); font-family: 'DM Mono', monospace; }

  /* KANBAN */
  .kanban { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 8px; }
  .kanban-col {
    min-width: 240px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r);
    padding: 16px;
    flex-shrink: 0;
  }
  .kanban-col-title { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 13px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
  .kanban-item {
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 8px;
    font-size: 12px;
    cursor: grab;
    transition: all var(--transition);
  }
  .kanban-item:hover { border-color: var(--neon); box-shadow: var(--glow); }

  /* Notification toast */
  @keyframes slideIn { from{transform:translateX(120%)} to{transform:translateX(0)} }
  .toast {
    position: fixed; bottom: 24px; right: 24px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    border-left: 3px solid var(--neon3);
    border-radius: var(--r2);
    padding: 12px 16px;
    font-size: 13px;
    z-index: 1000;
    animation: slideIn 0.3s ease;
    max-width: 300px;
  }

  /* GAUGE */
  .gauge-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; font-size: 13px; }
  .gauge-label { width: 100px; color: var(--text2); font-family: 'DM Mono', monospace; font-size: 11px; }
  .gauge-bar { flex: 1; height: 6px; background: var(--surface2); border-radius: 3px; overflow: hidden; }
  .gauge-fill { height: 100%; border-radius: 3px; transition: width 0.8s ease; }
  .gauge-val { width: 36px; text-align: right; font-size: 12px; font-family: 'DM Mono', monospace; color: var(--text2); }
`;

// ============================================================
// DATA
// ============================================================
const MODULES = [
  { id: "dashboard", label: "Dashboard", icon: "⬡" },
  { id: "edtech", label: "EdTech AI", icon: "🧠" },
  { id: "marketplace", label: "Marketplace", icon: "🛒" },
  { id: "fintech", label: "Fintech", icon: "📈" },
  { id: "meetings", label: "Meetings", icon: "🎙️" },
  { id: "tourism", label: "Tourism", icon: "🌌" },
  { id: "engineering", label: "Engineering", icon: "⚙️" },
  { id: "entertainment", label: "Entertainment", icon: "🎬" },
  { id: "mail", label: "Quantum Mail", icon: "✉️" },
  { id: "collab", label: "Collab Hub", icon: "🤝" },
  { id: "profile", label: "Profile", icon: "👤" },
  { id: "settings", label: "Settings", icon: "⚙️" },
];

const COURSES = [
  { id: 1, title: "Quantum Computing Fundamentals", category: "STEM", level: "Advanced", duration: "12h", rating: 4.9, students: 2841, progress: 65, color: "#7c3aed" },
  { id: 2, title: "AI Ethics & Governance", category: "Ethics", level: "Intermediate", duration: "8h", rating: 4.7, students: 1920, progress: 30, color: "#06b6d4" },
  { id: 3, title: "Multiverse Architecture", category: "Engineering", level: "Expert", duration: "20h", rating: 5.0, students: 542, progress: 0, color: "#10b981" },
  { id: 4, title: "Neural Interface Design", category: "Design", level: "Intermediate", duration: "10h", rating: 4.8, students: 3102, progress: 80, color: "#f59e0b" },
  { id: 5, title: "Space-Time Finance", category: "Finance", level: "Advanced", duration: "15h", rating: 4.6, students: 876, progress: 45, color: "#ec4899" },
  { id: 6, title: "Holographic UX", category: "Design", level: "Beginner", duration: "6h", rating: 4.5, students: 4231, progress: 100, color: "#7c3aed" },
];

const PRODUCTS = [
  { id: 1, name: "Quantum Headset Pro", price: 1299, category: "Hardware", rating: 4.9, emoji: "🥽", stock: 12 },
  { id: 2, name: "Neural Link Cable", price: 89, category: "Accessories", rating: 4.7, emoji: "🔌", stock: 248 },
  { id: 3, name: "AI Core Subscription", price: 49, category: "Software", rating: 4.8, emoji: "🤖", stock: 999 },
  { id: 4, name: "Holographic Display Unit", price: 4999, category: "Hardware", rating: 5.0, emoji: "📡", stock: 3 },
  { id: 5, name: "Quantum Encryption Key", price: 199, category: "Security", rating: 4.6, emoji: "🔑", stock: 87 },
  { id: 6, name: "Neural Haptic Gloves", price: 599, category: "Hardware", rating: 4.5, emoji: "🧤", stock: 34 },
];

const PORTFOLIO = [
  { symbol: "QTC", name: "Quantum Token", price: 4280.50, change: +12.4, alloc: 35, color: "#7c3aed" },
  { symbol: "NRL", name: "Neural Labs", price: 892.30, change: -2.1, alloc: 20, color: "#06b6d4" },
  { symbol: "MVX", name: "Multiverse X", price: 156.80, change: +8.7, alloc: 25, color: "#10b981" },
  { symbol: "HTK", name: "HoloTech", price: 2341.00, change: +1.3, alloc: 12, color: "#f59e0b" },
  { symbol: "STF", name: "SpaceTime Finance", price: 78.90, change: -0.4, alloc: 8, color: "#ec4899" },
];

const BADGES = [
  { emoji: "⚡", name: "Speed Learner", earned: true },
  { emoji: "🔮", name: "Quantum Pioneer", earned: true },
  { emoji: "🌟", name: "Star Contributor", earned: true },
  { emoji: "🤖", name: "AI Master", earned: true },
  { emoji: "🏆", name: "Champion", earned: false },
  { emoji: "🌌", name: "Multiverse Explorer", earned: false },
  { emoji: "💎", name: "Diamond Rank", earned: false },
  { emoji: "🦾", name: "Cybernetic", earned: false },
];

const TOUR_PACKAGES = [
  { id: 1, name: "Luna Station Alpha", type: "Space", price: 125000, duration: "14 days", emoji: "🌙", rating: 4.9 },
  { id: 2, name: "Neural Healing Retreat", type: "Medical", price: 8500, duration: "7 days", emoji: "🏥", rating: 4.7 },
  { id: 3, name: "Dimension Rift Experience", type: "Multiverse", price: 250000, duration: "3 days", emoji: "🌀", rating: 5.0 },
  { id: 4, name: "Mars Colony Tour", type: "Space", price: 75000, duration: "21 days", emoji: "🔴", rating: 4.8 },
];

const MEDIA = [
  { id: 1, title: "Quantum Dreams", type: "Series", episodes: 12, emoji: "🔮", bg: "linear-gradient(135deg,#7c3aed,#06b6d4)" },
  { id: 2, title: "Neural Storm", type: "Film", duration: "2h15m", emoji: "⚡", bg: "linear-gradient(135deg,#10b981,#06b6d4)" },
  { id: 3, title: "Multiverse Chronicles", type: "Documentary", episodes: 6, emoji: "🌌", bg: "linear-gradient(135deg,#f59e0b,#ec4899)" },
  { id: 4, title: "AI Uprising", type: "Series", episodes: 20, emoji: "🤖", bg: "linear-gradient(135deg,#ec4899,#7c3aed)" },
  { id: 5, title: "Luna Base", type: "Film", duration: "1h52m", emoji: "🌙", bg: "linear-gradient(135deg,#6d28d9,#10b981)" },
  { id: 6, title: "The Last Engineer", type: "Series", episodes: 8, emoji: "⚙️", bg: "linear-gradient(135deg,#06b6d4,#f59e0b)" },
];

const EMAILS = [
  { id: 1, from: "NOVA AI", subject: "Weekly Learning Report", preview: "You completed 3 courses this week...", time: "2h ago", read: false, emoji: "🤖" },
  { id: 2, from: "Quantum Finance", subject: "Portfolio Alert: QTC +12%", preview: "Your quantum token allocation...", time: "4h ago", read: false, emoji: "📈" },
  { id: 3, from: "Team Nexus", subject: "Project Blackhole Update", preview: "The engineering team has completed...", time: "1d ago", read: true, emoji: "⚙️" },
  { id: 4, from: "Luna Tour Co.", subject: "Booking Confirmed: Luna Station Alpha", preview: "Your journey to the moon begins...", time: "2d ago", read: true, emoji: "🌙" },
  { id: 5, from: "APEX Coach", subject: "New Achievement Unlocked!", preview: "Congratulations! You've earned...", time: "3d ago", read: true, emoji: "🏆" },
];

const KANBAN_DATA = {
  backlog: [
    { id: "k1", title: "Quantum mesh network integration", tag: "Engineering" },
    { id: "k2", title: "AI-powered content recommendations", tag: "AI" },
  ],
  inprogress: [
    { id: "k3", title: "Neural interface v2.0 design", tag: "Design" },
    { id: "k4", title: "Multi-dimensional database schema", tag: "Backend" },
  ],
  review: [
    { id: "k5", title: "Holographic meeting rooms", tag: "Feature" },
  ],
  done: [
    { id: "k6", title: "XP gamification system", tag: "Gamification" },
    { id: "k7", title: "Quantum encryption layer", tag: "Security" },
  ],
};

// ============================================================
// PARTICLE CANVAS
// ============================================================
function QuantumCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, particles = [];
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2 + 0.5,
        color: Math.random() > 0.5 ? "#7c3aed" : "#06b6d4",
        alpha: Math.random() * 0.6 + 0.2,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, "0");
        ctx.fill();
      });
      // connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(124,58,237,${0.15 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="quantum-canvas" />;
}

// ============================================================
// AI CHAT COMPONENT
// ============================================================
function AIChat({ aiName, aiColor, systemPrompt }) {
  const [messages, setMessages] = useState([
    { role: "ai", content: `Hello! I'm ${aiName}. How can I assist you today?` }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const history = messages.slice(-6).map(m => ({
        role: m.role === "ai" ? "assistant" : "user",
        content: m.content,
      }));
      history.push({ role: "user", content: userMsg });
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": process.env.ANTHROPIC_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: systemPrompt || `You are ${aiName}, an advanced AI assistant for the MVDL Quantum Superintelligence Platform. Be helpful, concise, and futuristic in tone.`,
          messages: history,
        }),
      });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "I'm having trouble processing that right now.";
      setMessages(prev => [...prev, { role: "ai", content: reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "ai", content: "Connection to quantum AI core disrupted. Please retry." }]);
    }
    setLoading(false);
  };

  return (
    <div className="chat-container">
      <div className="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role}`}>
            {m.role === "ai" && <div className="ai-label">◆ {aiName}</div>}
            {m.content}
          </div>
        ))}
        {loading && (
          <div className="chat-msg ai">
            <div className="ai-label">◆ {aiName}</div>
            <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="chat-input-row">
        <textarea
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={`Ask ${aiName}...`}
          rows={1}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button className="btn btn-primary btn-icon" onClick={send} disabled={loading}>→</button>
      </div>
    </div>
  );
}

// ============================================================
// MODULES
// ============================================================

function Dashboard({ xp, setXp }) {
  const stats = [
    { label: "Modules Active", value: "12", change: "+2 this week" },
    { label: "AI Interactions", value: "3,847", change: "+124 today" },
    { label: "XP Earned", value: xp.toLocaleString(), change: "+280 today" },
    { label: "Collaborators", value: "47", change: "+3 online" },
  ];
  const activity = [
    { text: "Completed: Neural Interface Design", time: "2m ago", icon: "✅" },
    { text: "OMEGA sent portfolio alert", time: "15m ago", icon: "📈" },
    { text: "New badge: Speed Learner", time: "1h ago", icon: "⚡" },
    { text: "Meeting: Quantum Project Alpha", time: "3h ago", icon: "🎙️" },
    { text: "Booked: Luna Station Alpha", time: "1d ago", icon: "🌙" },
  ];
  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Mission Control</div>
          <div className="module-subtitle">Your MVDL Quantum Platform overview</div>
        </div>
        <button className="btn btn-primary" onClick={() => setXp(x => x + 50)}>+ Earn XP</button>
      </div>

      <div className="section canvas-wrapper" style={{ height: 160, marginBottom: 24 }}>
        <QuantumCanvas />
        <div style={{ position: "relative", padding: 20, zIndex: 1 }}>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 28, fontWeight: 800, background: "linear-gradient(135deg,#e8e8f0,#06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
            Welcome back, Commander
          </div>
          <div style={{ color: "var(--text2)", marginTop: 6, fontSize: 14 }}>All quantum systems nominal. 3 AI cores active.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <span className="tag tag-green">● Systems Online</span>
            <span className="tag tag-cyan">● AI Active</span>
            <span className="tag tag-purple">● Quantum Sync</span>
          </div>
        </div>
      </div>

      <div className="grid-4 section">
        {stats.map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-change">{s.change}</div>
          </div>
        ))}
      </div>

      <div className="grid-2 section">
        <div className="card">
          <div className="card-title">📡 Recent Activity</div>
          {activity.map((a, i) => (
            <div key={i} className="list-item">
              <span style={{ fontSize: 18 }}>{a.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13 }}>{a.text}</div>
                <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono" }}>{a.time}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-title">🏅 Achievements</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            {BADGES.slice(0, 6).map((b, i) => (
              <div key={i} className={`badge ${b.earned ? "earned" : ""}`} title={b.name}>
                {b.emoji}
                {!b.earned && <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.5)" }} />}
              </div>
            ))}
          </div>
          <div className="form-label">XP Progress to Level 12</div>
          <div className="progress-bar"><div className="progress-fill" style={{ width: "72%" }} /></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono", marginTop: 4 }}>
            <span>{xp.toLocaleString()} XP</span><span>Next: 15,000</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">🤖 Ask NOVA — Platform AI</div>
        <AIChat aiName="NOVA" systemPrompt="You are NOVA, the central AI of the MVDL Quantum Superintelligence Platform. Help users navigate modules, explain features, and provide insights. Keep responses concise and helpful." />
      </div>
    </div>
  );
}

function EdTech({ xp, setXp }) {
  const [tab, setTab] = useState("courses");
  const [filter, setFilter] = useState("All");
  const categories = ["All", "STEM", "Design", "Finance", "Ethics", "Engineering"];
  const filtered = filter === "All" ? COURSES : COURSES.filter(c => c.category === filter);

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">EdTech AI Academy</div>
          <div className="module-subtitle">Adaptive learning powered by quantum intelligence</div>
        </div>
        <button className="btn btn-primary" onClick={() => setXp(x => x + 100)}>Create Course</button>
      </div>

      <div className="tabs">
        {["courses", "ai-tutor", "quiz", "leaderboard"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t === "courses" ? "📚 Courses" : t === "ai-tutor" ? "🤖 AI Tutor" : t === "quiz" ? "🧩 Quiz" : "🏆 Leaderboard"}
          </div>
        ))}
      </div>

      {tab === "courses" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {categories.map(c => (
              <button key={c} className={`btn ${filter === c ? "btn-primary" : "btn-secondary"} btn-sm`} onClick={() => setFilter(c)}>{c}</button>
            ))}
          </div>
          <div className="grid-auto">
            {filtered.map(c => (
              <div key={c.id} className="card" style={{ cursor: "pointer" }}>
                <div style={{ height: 6, borderRadius: 3, background: c.color, marginBottom: 16, marginTop: -4, marginLeft: -4, marginRight: -4 }} />
                <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{c.title}</div>
                <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                  <span className="tag tag-purple">{c.category}</span>
                  <span className="tag tag-cyan">{c.level}</span>
                  <span className="tag tag-green">{c.duration}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text2)", fontFamily: "DM Mono", marginBottom: 12 }}>
                  <span>⭐ {c.rating}</span>
                  <span>{c.students.toLocaleString()} students</span>
                </div>
                {c.progress > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono", marginBottom: 4 }}>Progress: {c.progress}%</div>
                    <div className="progress-bar"><div className="progress-fill" style={{ width: `${c.progress}%` }} /></div>
                  </>
                )}
                {c.progress === 0 && (
                  <button className="btn btn-secondary btn-sm" style={{ width: "100%", marginTop: 4 }} onClick={() => setXp(x => x + 50)}>Enroll Now</button>
                )}
                {c.progress === 100 && <div style={{ textAlign: "center", color: "var(--neon3)", fontSize: 12, fontFamily: "DM Mono" }}>✅ Completed</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "ai-tutor" && (
        <div className="card">
          <div className="card-title">🧠 NOVA Learning AI — Adaptive Tutor</div>
          <AIChat aiName="NOVA" systemPrompt="You are NOVA, an advanced AI tutor on the MVDL platform. Help students understand complex topics in quantum computing, AI, space technology, and multiverse theory. Be educational, patient, and engaging. Use analogies and examples. If asked something outside education, gently redirect." />
        </div>
      )}

      {tab === "quiz" && (
        <div className="card">
          <div className="card-title">🧩 Adaptive Quiz Engine</div>
          <QuizComponent setXp={setXp} />
        </div>
      )}

      {tab === "leaderboard" && (
        <div className="card">
          <div className="card-title">🏆 Global XP Leaderboard</div>
          <table className="data-table">
            <thead><tr><th>#</th><th>User</th><th>XP</th><th>Level</th><th>Badge</th></tr></thead>
            <tbody>
              {[
                { rank: 1, name: "Commander Atlas", xp: "48,290", level: 24, badge: "💎" },
                { rank: 2, name: "Dr. Priya Nova", xp: "41,100", level: 21, badge: "🔮" },
                { rank: 3, name: "Orion Vex", xp: "38,750", level: 20, badge: "⚡" },
                { rank: 4, name: "You", xp: xp.toLocaleString(), level: 11, badge: "🌟", isYou: true },
                { rank: 5, name: "Nyx Cyber", xp: "12,001", level: 10, badge: "🤖" },
              ].map(u => (
                <tr key={u.rank}>
                  <td><span style={{ color: u.rank <= 3 ? "var(--neon4)" : "var(--text2)", fontFamily: "DM Mono" }}>{u.rank}</span></td>
                  <td style={{ fontWeight: u.isYou ? 700 : 400, color: u.isYou ? "var(--neon2)" : "var(--text)" }}>{u.name}</td>
                  <td style={{ fontFamily: "DM Mono", color: "var(--neon3)" }}>{u.xp}</td>
                  <td><span className="tag tag-purple">Lv.{u.level}</span></td>
                  <td style={{ fontSize: 20 }}>{u.badge}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QuizComponent({ setXp }) {
  const questions = [
    { q: "What is quantum entanglement?", opts: ["Particle correlation", "Quantum tunneling", "Wave collapse", "Superposition"], correct: 0 },
    { q: "Which AI architecture powers GPT models?", opts: ["CNN", "RNN", "Transformer", "GAN"], correct: 2 },
    { q: "What does MVDL stand for?", opts: ["Machine Vision Deep Learning", "Multi-Verse Digital Layer", "Molecular Vector Data Logic", "None"], correct: 1 },
  ];
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const answer = (i) => {
    setSelected(i);
    if (i === questions[current].correct) setScore(s => s + 1);
    setTimeout(() => {
      if (current < questions.length - 1) { setCurrent(c => c + 1); setSelected(null); }
      else { setDone(true); setXp(x => x + score * 50 + 100); }
    }, 1000);
  };

  if (done) return (
    <div style={{ textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 48 }}>🏆</div>
      <div style={{ fontFamily: "Syne", fontSize: 24, fontWeight: 800, margin: "12px 0" }}>Quiz Complete!</div>
      <div style={{ color: "var(--neon3)", fontFamily: "DM Mono", fontSize: 16 }}>{score}/{questions.length} Correct</div>
      <div style={{ color: "var(--neon4)", marginTop: 8 }}>+{score * 50 + 100} XP Earned</div>
      <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => { setCurrent(0); setScore(0); setDone(false); setSelected(null); }}>Retry</button>
    </div>
  );

  const q = questions[current];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontFamily: "DM Mono", fontSize: 12, color: "var(--text2)" }}>
        <span>Question {current + 1}/{questions.length}</span>
        <span>Score: {score}</span>
      </div>
      <div className="progress-bar" style={{ marginBottom: 20 }}><div className="progress-fill" style={{ width: `${((current) / questions.length) * 100}%` }} /></div>
      <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginBottom: 20 }}>{q.q}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {q.opts.map((o, i) => {
          let style = {};
          if (selected !== null) {
            if (i === q.correct) style = { borderColor: "var(--neon3)", background: "rgba(16,185,129,0.1)" };
            else if (i === selected) style = { borderColor: "var(--neon5)", background: "rgba(236,72,153,0.1)" };
          }
          return (
            <button key={i} className="btn btn-secondary" style={{ justifyContent: "flex-start", ...style }} onClick={() => answer(i)} disabled={selected !== null}>
              {String.fromCharCode(65 + i)}. {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Marketplace() {
  const [cart, setCart] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [desc, setDesc] = useState({});

  const addToCart = (p) => setCart(c => [...c, p]);
  const total = cart.reduce((a, p) => a + p.price, 0);

  const generateDesc = async (product) => {
    setGenerating(product.id);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": process.env.ANTHROPIC_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 150,
          messages: [{ role: "user", content: `Write a 2-sentence futuristic product description for: "${product.name}" priced at $${product.price}. Make it sound cutting-edge and desirable.` }],
        }),
      });
      const data = await res.json();
      setDesc(d => ({ ...d, [product.id]: data.content?.[0]?.text }));
    } catch { setDesc(d => ({ ...d, [product.id]: "Error generating description." })); }
    setGenerating(null);
  };

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Marketplace</div>
          <div className="module-subtitle">Next-gen technology, curated by AI</div>
        </div>
        <div className="xp-pill">🛒 {cart.length} items — ${total.toLocaleString()}</div>
      </div>
      <div className="grid-auto">
        {PRODUCTS.map(p => (
          <div key={p.id} className="card">
            <div style={{ fontSize: 48, textAlign: "center", padding: "16px 0" }}>{p.emoji}</div>
            <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{p.name}</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span className="tag tag-cyan">{p.category}</span>
              <span style={{ color: "var(--text2)", fontSize: 12, fontFamily: "DM Mono" }}>Stock: {p.stock}</span>
            </div>
            <div style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--text2)", marginBottom: 4 }}>⭐ {p.rating}</div>
            {desc[p.id] ? (
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 12, lineHeight: 1.5, fontStyle: "italic" }}>{desc[p.id]}</div>
            ) : (
              <button className="btn btn-ghost btn-sm" style={{ width: "100%", marginBottom: 8 }} onClick={() => generateDesc(p)} disabled={generating === p.id}>
                {generating === p.id ? <span className="pulse">Generating...</span> : "✨ AI Describe"}
              </button>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontFamily: "Syne", fontSize: 18, fontWeight: 800, color: "var(--neon2)" }}>${p.price.toLocaleString()}</div>
              <button className="btn btn-primary btn-sm" onClick={() => addToCart(p)}>Add to Cart</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Fintech() {
  const [tab, setTab] = useState("portfolio");
  const total = PORTFOLIO.reduce((a, p) => a + p.price * (p.alloc / 100) * 10, 0);

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Finance</div>
          <div className="module-subtitle">Multi-dimensional portfolio intelligence</div>
        </div>
        <div className="stat-card" style={{ padding: "10px 16px", minWidth: 160 }}>
          <div className="stat-label">Net Worth</div>
          <div className="stat-value" style={{ fontSize: 20 }}>${total.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</div>
          <div className="stat-change">+8.2% this month</div>
        </div>
      </div>

      <div className="tabs">
        {["portfolio", "analytics", "omega-ai"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t === "portfolio" ? "📊 Portfolio" : t === "analytics" ? "📈 Analytics" : "🤖 OMEGA AI"}
          </div>
        ))}
      </div>

      {tab === "portfolio" && (
        <div>
          <div className="card">
            <div className="card-title">💼 Holdings</div>
            <table className="data-table">
              <thead><tr><th>Asset</th><th>Price</th><th>24h Change</th><th>Allocation</th></tr></thead>
              <tbody>
                {PORTFOLIO.map(p => (
                  <tr key={p.symbol}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.symbol}</div>
                      <div style={{ fontSize: 11, color: "var(--text2)" }}>{p.name}</div>
                    </td>
                    <td style={{ fontFamily: "DM Mono" }}>${p.price.toLocaleString()}</td>
                    <td style={{ fontFamily: "DM Mono", color: p.change > 0 ? "var(--neon3)" : "var(--neon5)" }}>
                      {p.change > 0 ? "+" : ""}{p.change}%
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: "var(--surface2)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${p.alloc}%`, height: "100%", background: p.color, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--text2)" }}>{p.alloc}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "analytics" && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">📉 Risk Analysis</div>
            {[
              { label: "Volatility", val: 68, color: "var(--neon5)" },
              { label: "Sharpe Ratio", val: 82, color: "var(--neon3)" },
              { label: "Beta", val: 45, color: "var(--neon2)" },
              { label: "Alpha", val: 91, color: "var(--neon4)" },
            ].map(r => (
              <div key={r.label} className="gauge-row">
                <span className="gauge-label">{r.label}</span>
                <div className="gauge-bar"><div className="gauge-fill" style={{ width: `${r.val}%`, background: r.color }} /></div>
                <span className="gauge-val">{r.val}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title">🎯 Allocation Targets</div>
            {PORTFOLIO.map(p => (
              <div key={p.symbol} className="gauge-row">
                <span className="gauge-label">{p.symbol}</span>
                <div className="gauge-bar"><div className="gauge-fill" style={{ width: `${p.alloc}%`, background: p.color }} /></div>
                <span className="gauge-val">{p.alloc}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "omega-ai" && (
        <div className="card">
          <div className="card-title">🤖 OMEGA — Financial Intelligence AI</div>
          <AIChat aiName="OMEGA" systemPrompt="You are OMEGA, an advanced AI financial advisor on the MVDL platform. Provide insights on quantum asset portfolios, DeFi strategies, space economy investments, and multi-dimensional risk management. Be analytical and use realistic-sounding futuristic finance terminology. Keep answers concise and actionable." />
        </div>
      )}
    </div>
  );
}

function Meetings() {
  const [inCall, setInCall] = useState(false);
  const [time, setTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const [vidOff, setVidOff] = useState(false);
  const timerRef = useRef(null);

  const startCall = () => { setInCall(true); setTime(0); timerRef.current = setInterval(() => setTime(t => t + 1), 1000); };
  const endCall = () => { setInCall(false); clearInterval(timerRef.current); };
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const upcoming = [
    { title: "Quantum Project Alpha Standup", time: "Today 14:00", participants: 8, type: "Holographic" },
    { title: "Neural Interface Review", time: "Today 16:30", participants: 5, type: "Standard" },
    { title: "Multiverse Architecture Sprint", time: "Tomorrow 09:00", participants: 12, type: "Multiverse" },
  ];

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Meetings</div>
          <div className="module-subtitle">Holographic & multiverse collaboration</div>
        </div>
        {!inCall && <button className="btn btn-primary" onClick={startCall}>🎙️ Start Call</button>}
      </div>

      {inCall && (
        <div className="card glow-border section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16 }}>Quantum Meeting Active</div>
              <div style={{ fontFamily: "DM Mono", color: "var(--neon3)", fontSize: 14 }}>● LIVE {fmt(time)}</div>
            </div>
            <span className="tag tag-purple">Holographic Mode</span>
          </div>
          <div className="grid-3" style={{ marginBottom: 16 }}>
            {["Commander Atlas", "Dr. Nova", "Orion Vex"].map(name => (
              <div key={name} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: 20, textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,var(--neon),var(--neon2))", margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                  {name[0]}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{name}</div>
                <div style={{ fontSize: 10, color: "var(--neon3)", fontFamily: "DM Mono" }}>● Connected</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button className={`btn ${muted ? "btn-primary" : "btn-secondary"}`} onClick={() => setMuted(m => !m)}>{muted ? "🔇 Unmute" : "🎙️ Mute"}</button>
            <button className={`btn ${vidOff ? "btn-primary" : "btn-secondary"}`} onClick={() => setVidOff(v => !v)}>{vidOff ? "📷 Cam On" : "📹 Cam Off"}</button>
            <button className="btn btn-secondary">🖥️ Share Screen</button>
            <button className="btn" style={{ background: "rgba(236,72,153,0.2)", color: "var(--neon5)", border: "1px solid rgba(236,72,153,0.4)" }} onClick={endCall}>✕ End</button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">📅 Upcoming Meetings</div>
        {upcoming.map((m, i) => (
          <div key={i} className="list-item">
            <div style={{ width: 40, height: 40, borderRadius: 8, background: "var(--surface2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🎙️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{m.title}</div>
              <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono" }}>{m.time} · {m.participants} participants</div>
            </div>
            <span className={`tag ${m.type === "Holographic" ? "tag-purple" : m.type === "Multiverse" ? "tag-pink" : "tag-cyan"}`}>{m.type}</span>
            <button className="btn btn-secondary btn-sm">Join</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tourism() {
  const [tab, setTab] = useState("packages");
  const [selected, setSelected] = useState(null);
  const [aiGuide, setAiGuide] = useState(null);
  const [loading, setLoading] = useState(false);

  const getAIGuide = async (pkg) => {
    setLoading(true);
    setSelected(pkg);
    setAiGuide(null);
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": process.env.ANTHROPIC_KEY, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 300,
          messages: [{ role: "user", content: `Write a captivating 3-paragraph AI travel guide for the "${pkg.name}" package (type: ${pkg.type}, duration: ${pkg.duration}, price: $${pkg.price.toLocaleString()}). Make it immersive and futuristic.` }],
        }),
      });
      const data = await res.json();
      setAiGuide(data.content?.[0]?.text);
    } catch { setAiGuide("AI travel guide unavailable. Please try again."); }
    setLoading(false);
  };

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Tourism</div>
          <div className="module-subtitle">Space, medical, and multiverse experiences</div>
        </div>
      </div>

      <div className="tabs">
        {["packages", "my-trips"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t === "packages" ? "🌌 Packages" : "🧳 My Trips"}
          </div>
        ))}
      </div>

      {tab === "packages" && (
        <div className="grid-2">
          {TOUR_PACKAGES.map(pkg => (
            <div key={pkg.id} className="card" style={{ cursor: "pointer" }}>
              <div style={{ fontSize: 48, textAlign: "center", padding: "16px 0" }}>{pkg.emoji}</div>
              <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{pkg.name}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <span className={`tag ${pkg.type === "Space" ? "tag-cyan" : pkg.type === "Multiverse" ? "tag-purple" : "tag-green"}`}>{pkg.type}</span>
                <span className="tag tag-amber">{pkg.duration}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontFamily: "Syne", fontSize: 20, fontWeight: 800, color: "var(--neon2)" }}>${pkg.price.toLocaleString()}</div>
                <span style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--text2)" }}>⭐ {pkg.rating}</span>
              </div>
              <button className="btn btn-secondary btn-sm" style={{ width: "100%", marginBottom: 8 }} onClick={() => getAIGuide(pkg)} disabled={loading && selected?.id === pkg.id}>
                {loading && selected?.id === pkg.id ? <span className="pulse">Generating AI Guide...</span> : "✨ AI Travel Guide"}
              </button>
              <button className="btn btn-primary btn-sm" style={{ width: "100%" }}>Book Now</button>
              {selected?.id === pkg.id && aiGuide && (
                <div style={{ marginTop: 12, padding: 12, background: "var(--bg)", borderRadius: 8, border: "1px solid var(--border)", fontSize: 12, lineHeight: 1.7, color: "var(--text2)" }}>
                  <div style={{ fontFamily: "DM Mono", fontSize: 10, color: "var(--neon2)", marginBottom: 6 }}>◆ AI TRAVEL GUIDE</div>
                  {aiGuide}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "my-trips" && (
        <div className="card">
          <div className="card-title">🧳 My Bookings</div>
          <div className="list-item">
            <span style={{ fontSize: 28 }}>🌙</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>Luna Station Alpha</div>
              <div style={{ fontSize: 12, color: "var(--text2)", fontFamily: "DM Mono" }}>Departure: April 15, 2026 · Confirmation: QT-8821</div>
            </div>
            <span className="tag tag-green">Confirmed</span>
          </div>
          <div style={{ padding: 32, textAlign: "center", color: "var(--text2)", fontSize: 13 }}>No other bookings yet</div>
        </div>
      )}
    </div>
  );
}

function Engineering() {
  const projects = [
    { name: "Project Blackhole", status: "Active", completion: 72, team: 6, priority: "Critical" },
    { name: "Neural Mesh v3", status: "Review", completion: 91, team: 3, priority: "High" },
    { name: "Quantum Router Mk2", status: "Planning", completion: 15, team: 8, priority: "Medium" },
  ];
  const iot = [
    { name: "Quantum Processor Array", status: "online", load: 78 },
    { name: "Neural Interface Hub", status: "online", load: 45 },
    { name: "Holographic Renderer", status: "warning", load: 93 },
    { name: "Space Comm Relay", status: "offline", load: 0 },
  ];

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Engineering Hub</div>
          <div className="module-subtitle">Project management & IoT monitoring</div>
        </div>
        <button className="btn btn-primary">+ New Project</button>
      </div>

      <div className="card section">
        <div className="card-title">🚀 Active Projects</div>
        {projects.map(p => (
          <div key={p.name} className="list-item" style={{ flexDirection: "column", alignItems: "stretch" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono" }}>{p.team} engineers</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className={`tag ${p.priority === "Critical" ? "tag-pink" : p.priority === "High" ? "tag-amber" : "tag-cyan"}`}>{p.priority}</span>
                <span className={`tag ${p.status === "Active" ? "tag-green" : p.status === "Review" ? "tag-purple" : "tag-cyan"}`}>{p.status}</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="progress-bar" style={{ flex: 1 }}><div className="progress-fill" style={{ width: `${p.completion}%` }} /></div>
              <span style={{ fontFamily: "DM Mono", fontSize: 11, color: "var(--text2)", width: 32 }}>{p.completion}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-title">📡 IoT System Monitor</div>
        {iot.map(d => (
          <div key={d.name} className="gauge-row">
            <span className="gauge-label">{d.name.substring(0, 12)}…</span>
            <div className="gauge-bar">
              <div className="gauge-fill" style={{ width: `${d.load}%`, background: d.status === "offline" ? "#555" : d.status === "warning" ? "var(--neon4)" : "var(--neon3)" }} />
            </div>
            <span className="gauge-val" style={{ color: d.status === "offline" ? "#555" : d.status === "warning" ? "var(--neon4)" : "var(--neon3)" }}>
              {d.status === "offline" ? "OFF" : `${d.load}%`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Entertainment() {
  const [playing, setPlaying] = useState(null);

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Entertainment</div>
          <div className="module-subtitle">Holographic media from across the multiverse</div>
        </div>
      </div>

      {playing && (
        <div className="card section" style={{ textAlign: "center" }}>
          <div style={{ fontSize: 64, margin: "20px 0" }}>{playing.emoji}</div>
          <div style={{ fontFamily: "Syne", fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{playing.title}</div>
          <div style={{ color: "var(--text2)", fontSize: 13, marginBottom: 16 }}>{playing.type} · {playing.episodes ? `${playing.episodes} episodes` : playing.duration}</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button className="btn btn-secondary">⏮ Prev</button>
            <button className="btn btn-primary">⏸ Pause</button>
            <button className="btn btn-secondary">Next ⏭</button>
            <button className="btn btn-ghost" onClick={() => setPlaying(null)}>✕ Close</button>
          </div>
        </div>
      )}

      <div className="grid-3">
        {MEDIA.map(m => (
          <div key={m.id} className="media-card" onClick={() => setPlaying(m)}>
            <div className="media-thumb" style={{ background: m.bg }}>
              <span style={{ fontSize: 56 }}>{m.emoji}</span>
              <div style={{ position: "absolute", top: 8, right: 8 }}><span className="tag tag-purple">{m.type}</span></div>
            </div>
            <div className="media-info">
              <div className="media-title">{m.title}</div>
              <div className="media-meta">{m.episodes ? `${m.episodes} episodes` : m.duration}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuantumMail() {
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Quantum Mail</div>
          <div className="module-subtitle">Quantum-encrypted interdimensional messaging</div>
        </div>
        <button className="btn btn-primary" onClick={() => setComposing(true)}>+ Compose</button>
      </div>

      {composing && (
        <div className="card section">
          <div className="card-title">✉️ New Message</div>
          <div className="form-group"><label className="form-label">To</label><input className="input" placeholder="Recipient quantum address" /></div>
          <div className="form-group"><label className="form-label">Subject</label><input className="input" placeholder="Message subject" /></div>
          <div className="form-group"><label className="form-label">Message</label><textarea className="input" rows={4} placeholder="Compose your quantum message..." /></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary">⚡ Send Encrypted</button>
            <button className="btn btn-ghost" onClick={() => setComposing(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="col-2">
        <div className="card" style={{ flex: "0 0 320px" }}>
          <div className="card-title">📥 Inbox</div>
          {EMAILS.map(e => (
            <div key={e.id} className="list-item" style={{ cursor: "pointer", flexDirection: "column", alignItems: "stretch", gap: 4, background: selected?.id === e.id ? "var(--surface2)" : "transparent", padding: "10px 8px", borderRadius: 8 }} onClick={() => setSelected(e)}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ fontWeight: e.read ? 400 : 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                  {!e.read && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--neon2)", display: "inline-block" }} />}
                  {e.from}
                </div>
                <span style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono" }}>{e.time}</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: e.read ? 400 : 600 }}>{e.subject}</div>
              <div style={{ fontSize: 11, color: "var(--text2)" }}>{e.preview}</div>
            </div>
          ))}
        </div>
        <div className="card">
          {selected ? (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 22 }}>{selected.emoji}</div>
                <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, marginTop: 8 }}>{selected.subject}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", fontFamily: "DM Mono", marginTop: 4 }}>From: {selected.from} · {selected.time}</div>
              </div>
              <hr className="divider" />
              <div style={{ fontSize: 14, lineHeight: 1.8, color: "var(--text2)" }}>
                {selected.preview} This is a quantum-encrypted message from the MVDL network. All communications are secured with multi-dimensional encryption protocols ensuring zero-knowledge privacy across all dimensional channels.
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary btn-sm">↩ Reply</button>
                <button className="btn btn-secondary btn-sm">↪ Forward</button>
                <button className="btn btn-ghost btn-sm">🗑 Delete</button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 48, color: "var(--text2)", fontSize: 14 }}>Select a message to read</div>
          )}
        </div>
      </div>
    </div>
  );
}

function CollabHub() {
  const [tab, setTab] = useState("kanban");

  return (
    <div>
      <div className="module-header">
        <div>
          <div className="module-title">Collaboration Hub</div>
          <div className="module-subtitle">Real-time team workspace with AI coaching</div>
        </div>
      </div>

      <div className="tabs">
        {["kanban", "chat", "apex-ai"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t === "kanban" ? "📋 Task Board" : t === "chat" ? "💬 Team Chat" : "🤖 APEX Coach"}
          </div>
        ))}
      </div>

      {tab === "kanban" && (
        <div className="kanban">
          {[
            { key: "backlog", title: "Backlog", color: "var(--text2)" },
            { key: "inprogress", title: "In Progress", color: "var(--neon2)" },
            { key: "review", title: "Review", color: "var(--neon4)" },
            { key: "done", title: "Done", color: "var(--neon3)" },
          ].map(col => (
            <div key={col.key} className="kanban-col">
              <div className="kanban-col-title">
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, display: "inline-block" }} />
                {col.title}
                <span style={{ fontSize: 11, fontFamily: "DM Mono", color: "var(--text2)", marginLeft: "auto" }}>{KANBAN_DATA[col.key].length}</span>
              </div>
              {KANBAN_DATA[col.key].map(item => (
                <div key={item.id} className="kanban-item">
                  <div style={{ fontWeight: 500, marginBottom: 6 }}>{item.title}</div>
                  <span className="tag tag-cyan">{item.tag}</span>
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" style={{ width: "100%", marginTop: 4 }}>+ Add</button>
            </div>
          ))}
        </div>
      )}

      {tab === "chat" && (
        <div className="card">
          <div className="card-title">💬 Team Nexus Chat</div>
          <div className="chat-container">
            <div className="chat-messages">
              {[
                { name: "Commander Atlas", msg: "Quantum mesh integration is 72% complete", time: "14:22", color: "#7c3aed" },
                { name: "Dr. Nova", msg: "Neural interface tests passed phase 2!", time: "14:35", color: "#06b6d4" },
                { name: "Orion Vex", msg: "Holographic display latency reduced to 4ms", time: "14:41", color: "#10b981" },
              ].map((m, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontFamily: "DM Mono", color: m.color, marginBottom: 4 }}>{m.name} · {m.time}</div>
                  <div style={{ background: "var(--surface2)", padding: "8px 12px", borderRadius: 8, fontSize: 13, display: "inline-block" }}>{m.msg}</div>
                </div>
              ))}
            </div>
            <div className="chat-input-row">
              <input className="chat-input" style={{ height: 36 }} placeholder="Message Team Nexus..." />
              <button className="btn btn-primary btn-icon">→</button>
            </div>
          </div>
        </div>
      )}

      {tab === "apex-ai" && (
        <div className="card">
          <div className="card-title">🤖 APEX — Team AI Coach</div>
          <AIChat aiName="APEX" systemPrompt="You are APEX, an AI team coach on the MVDL platform. Help teams with project management, conflict resolution, productivity optimization, and collaboration strategies. Be supportive, data-driven, and actionable. Use agile and quantum management terminology." />
        </div>
      )}
    </div>
  );
}

function Profile({ xp }) {
  const level = Math.floor(xp / 1000) + 1;
  return (
    <div>
      <div className="module-header">
        <div><div className="module-title">Commander Profile</div></div>
        <button className="btn btn-secondary">Edit Profile</button>
      </div>

      <div className="grid-2">
        <div className="card" style={{ textAlign: "center" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg,var(--neon),var(--neon2))", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>⚡</div>
          <div style={{ fontFamily: "Syne", fontSize: 22, fontWeight: 800 }}>Commander Alpha</div>
          <div style={{ color: "var(--text2)", fontSize: 13, margin: "4px 0 12px" }}>Quantum Strategist · Level {level}</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            <span className="tag tag-purple">Quantum Pioneer</span>
            <span className="tag tag-cyan">AI Master</span>
            <span className="tag tag-green">Speed Learner</span>
          </div>
          <hr className="divider" />
          <div className="grid-3" style={{ textAlign: "center" }}>
            {[{ label: "XP", value: xp.toLocaleString() }, { label: "Level", value: level }, { label: "Badges", value: 4 }].map(s => (
              <div key={s.label}>
                <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, color: "var(--neon2)" }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "var(--text2)", fontFamily: "DM Mono" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-title">🏅 Badges & Achievements</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {BADGES.map((b, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div className={`badge ${b.earned ? "earned" : ""}`} style={{ margin: "0 auto 4px" }}>
                  {b.emoji}
                  {!b.earned && <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🔒</span>}
                </div>
                <div style={{ fontSize: 9, color: b.earned ? "var(--text2)" : "#444", fontFamily: "DM Mono", maxWidth: 56, wordBreak: "break-word" }}>{b.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title">📊 Module Activity</div>
        <div className="grid-3">
          {[
            { module: "EdTech", score: 89 },
            { module: "Fintech", score: 72 },
            { module: "Engineering", score: 65 },
            { module: "Tourism", score: 41 },
            { module: "Entertainment", score: 88 },
            { module: "Collab", score: 77 },
          ].map(m => (
            <div key={m.module} className="gauge-row">
              <span className="gauge-label">{m.module}</span>
              <div className="gauge-bar"><div className="gauge-fill" style={{ width: `${m.score}%` }} /></div>
              <span className="gauge-val">{m.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Settings() {
  const [toggles, setToggles] = useState({ notifications: true, aiAssist: true, quantumSync: true, holographic: false, analytics: true, twoFactor: false });
  const toggle = k => setToggles(t => ({ ...t, [k]: !t[k] }));

  return (
    <div>
      <div className="module-header">
        <div><div className="module-title">Platform Settings</div><div className="module-subtitle">Customize your quantum experience</div></div>
      </div>
      <div className="grid-2">
        <div className="card">
          <div className="card-title">👤 Account</div>
          <div className="form-group"><label className="form-label">Display Name</label><input className="input" defaultValue="Commander Alpha" /></div>
          <div className="form-group"><label className="form-label">Quantum Address</label><input className="input" defaultValue="alpha@mvdl.quantum" /></div>
          <div className="form-group"><label className="form-label">Timezone</label>
            <select className="input"><option>UTC+0 — London</option><option>UTC+8 — Singapore</option><option>UTC-5 — New York</option></select>
          </div>
          <button className="btn btn-primary">Save Changes</button>
        </div>
        <div className="card">
          <div className="card-title">⚙️ Preferences</div>
          {[
            { key: "notifications", label: "Push Notifications", desc: "Receive quantum alerts" },
            { key: "aiAssist", label: "AI Assistance", desc: "Enable AI across all modules" },
            { key: "quantumSync", label: "Quantum Sync", desc: "Real-time data synchronization" },
            { key: "holographic", label: "Holographic Mode", desc: "Enable 3D holographic UI" },
            { key: "analytics", label: "Analytics", desc: "Share usage analytics" },
            { key: "twoFactor", label: "2FA Security", desc: "Quantum two-factor auth" },
          ].map(s => (
            <div key={s.key} className="toggle-row">
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: "var(--text2)" }}>{s.desc}</div>
              </div>
              <div className={`toggle ${toggles[s.key] ? "on" : ""}`} onClick={() => toggle(s.key)} />
            </div>
          ))}
        </div>
      </div>
      <div className="card" style={{ marginTop: 16, border: "1px solid rgba(236,72,153,0.3)" }}>
        <div className="card-title" style={{ color: "var(--neon5)" }}>⚠️ Danger Zone</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div><div style={{ fontSize: 13 }}>Delete Account</div><div style={{ fontSize: 11, color: "var(--text2)" }}>This action cannot be undone across all dimensions</div></div>
          <button className="btn" style={{ background: "rgba(236,72,153,0.15)", color: "var(--neon5)", border: "1px solid rgba(236,72,153,0.4)" }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// APP SHELL
// ============================================================
export default function App() {
  const [module, setModule] = useState("dashboard");
  const [xp, setXp] = useState(10840);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const nav = (id) => {
    setModule(id);
    if (id !== module) { setXp(x => x + 10); showToast(`+10 XP — Switched to ${MODULES.find(m => m.id === id)?.label}`); }
  };

  const activeModule = MODULES.find(m => m.id === module);

  const moduleMap = {
    dashboard: <Dashboard xp={xp} setXp={setXp} />,
    edtech: <EdTech xp={xp} setXp={setXp} />,
    marketplace: <Marketplace />,
    fintech: <Fintech />,
    meetings: <Meetings />,
    tourism: <Tourism />,
    engineering: <Engineering />,
    entertainment: <Entertainment />,
    mail: <QuantumMail />,
    collab: <CollabHub />,
    profile: <Profile xp={xp} />,
    settings: <Settings />,
  };

  const mobileModules = MODULES.slice(0, 5);

  return (
    <div className="app">
      <aside className={`sidebar ${sidebarOpen ? "expanded" : ""}`} onMouseEnter={() => setSidebarOpen(true)} onMouseLeave={() => setSidebarOpen(false)}>
        <div className="sidebar-logo" onClick={() => nav("dashboard")}>
          {sidebarOpen ? (<>MV<span>DL</span></>) : "M"}
        </div>
        {MODULES.map(m => (
          <div key={m.id} className={`nav-item ${module === m.id ? "active" : ""}`} onClick={() => nav(m.id)}>
            <span className="nav-icon">{m.icon}</span>
            {sidebarOpen && <span className="nav-label">{m.label}</span>}
          </div>
        ))}
      </aside>

      <main className="main">
        <header className="topbar">
          <div className="topbar-title">{activeModule?.icon} {activeModule?.label}</div>
          <div className="topbar-search">
            <span style={{ color: "var(--text2)" }}>⌕</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search quantum platform..." />
          </div>
          <div className="xp-pill">⚡ {xp.toLocaleString()} XP</div>
          <div className="avatar">CA</div>
        </header>

        <div className="content">
          {moduleMap[module] || <div style={{ color: "var(--text2)", padding: 40, textAlign: "center" }}>Module coming soon...</div>}
        </div>
      </main>

      <nav className="mobile-nav">
        {mobileModules.map(m => (
          <div key={m.id} className={`mobile-nav-btn ${module === m.id ? "active" : ""}`} onClick={() => nav(m.id)}>
            <span>{m.icon}</span>
            <span>{m.label.split(" ")[0]}</span>
          </div>
        ))}
      </nav>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
